import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ConvexProvider, ConvexReactClient } from 'convex/react';
import { ConvexAuthProvider } from '@convex-dev/auth/react';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from '@posthog/react';
import { ToastProvider } from './components/Toast';
import App from './App';
import './index.css';

// Initialize Sentry for error tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance monitoring sample rate
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session replay sample rate
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Don't send errors in development unless DSN is explicitly set
    enabled: import.meta.env.PROD || !!import.meta.env.VITE_SENTRY_DSN,
  });
}

// Initialize PostHog with tracking DISABLED by default (GDPR compliance)
// Tracking is enabled only after user consent via the AnalyticsConsent component
if (import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false, // Disabled until consent
    capture_pageleave: false, // Disabled until consent
    opt_out_capturing_by_default: true, // GDPR: opt-out by default
    persistence: 'localStorage',
    respect_dnt: true, // Respect Do Not Track browser setting
  });
}

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);

/**
 * Custom storage adapter that uses sessionStorage with localStorage fallback.
 * This helps with Safari's ITP (Intelligent Tracking Prevention) which can
 * clear localStorage during cross-domain OAuth redirects (like Apple Sign In).
 *
 * sessionStorage is scoped to the browser tab and is less likely to be
 * affected by ITP during the OAuth callback redirect.
 */
const authStorage = {
  getItem: (key: string) => {
    // Try sessionStorage first, then localStorage as fallback
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  },
  setItem: (key: string, value: string) => {
    // Store in both to ensure persistence
    sessionStorage.setItem(key, value);
    localStorage.setItem(key, value);
  },
  removeItem: (key: string) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  },
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <ConvexProvider client={convex}>
        <ConvexAuthProvider client={convex} storage={authStorage}>
          <ToastProvider>
            <App />
          </ToastProvider>
        </ConvexAuthProvider>
      </ConvexProvider>
    </PostHogProvider>
  </StrictMode>
);
